import { useEffect, useRef, useState } from 'react';
import type { CtaBannerContent } from '~/content/types';
import { defaultCtaBanner } from '~/content/shared';
import { getAppCta } from '~/content/navigation';
import { ButtonLink, Container, DigitReel, FRAC_DELAY_MS } from '~/components/ui';

// The rating figure that leads the banner title ("4.4 of 5.0 …"). The integer
// and fractional digits each roll up in their own odometer reel while the "."
// and the trailing copy stay static. Kept to one decimal place.
const RATING_TOKEN = '4.4';
const [INT_DIGIT, FRAC_DIGIT] = RATING_TOKEN.split('.').map(Number);

/** Dark red pre-footer band with the "Get the app" CTA (Figma 27:6529). */
export function CtaBanner({ content = defaultCtaBanner }: { content?: CtaBannerContent }) {
  // The banner sits above the footer, so it's off-screen on load; gate the reel
  // roll on scroll-into-view (like TestimonialGrid) instead of firing on mount.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [play, setPlay] = useState(false);

  // The rating only animates when the title actually starts with "4.4"; any
  // per-page override without that prefix renders as plain static text.
  const rest = content.title.startsWith(RATING_TOKEN)
    ? content.title.slice(RATING_TOKEN.length)
    : null;

  useEffect(() => {
    if (rest === null) return;
    const el = headingRef.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setPlay(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rest]);

  return (
    <section>
      <Container>
        <div className="flex flex-col items-center gap-10 bg-red-900 px-5 pt-24 pb-[100px] text-center">
          <h2
            ref={headingRef}
            className="max-w-md text-h3 font-semibold tracking-[-0.03em] text-white md:text-h2">
            {rest === null ? (
              content.title
            ) : (
              <>
                {/* Static value for assistive tech; the reels are decorative. */}
                <span className="sr-only">{RATING_TOKEN}</span>
                <DigitReel target={INT_DIGIT} play={play} />
                {/* Static decimal point — never animates. */}
                <span aria-hidden>.</span>
                <DigitReel target={FRAC_DIGIT} delayMs={FRAC_DELAY_MS} play={play} />
                {rest}
              </>
            )}
          </h2>
          <ButtonLink href={getAppCta.href}>{getAppCta.label}</ButtonLink>
        </div>
      </Container>
    </section>
  );
}
